/**
 * Documents API
 *
 * API functions for document CRUD operations and version history.
 */

import { api } from "./client";
import type {
  ProjectId,
  DocumentId,
  Document,
  DocumentVersion,
  DocumentListResponse,
  DocumentVersionListResponse,
  CreateDocumentRequest,
  UpdateDocumentRequest,
  LinkedWorkItem,
  LinkedDocument,
  WorkItemId,
  ScanRepoDocumentsResponse,
  ImportDocumentsRequest,
  ImportDocumentsResponse,
  ImportDocumentResult,
} from "./types";

export async function listDocuments(
  projectId: ProjectId,
): Promise<DocumentListResponse> {
  return api.get<DocumentListResponse>(`/projects/${projectId}/documents`);
}

export async function getDocument(
  projectId: ProjectId,
  id: DocumentId,
): Promise<Document> {
  return api.get<Document>(`/projects/${projectId}/documents/${id}`);
}

export async function createDocument(
  projectId: ProjectId,
  req: CreateDocumentRequest,
): Promise<Document> {
  return api.post<Document>(`/projects/${projectId}/documents`, req);
}

export async function updateDocument(
  projectId: ProjectId,
  id: DocumentId,
  req: UpdateDocumentRequest,
): Promise<Document> {
  return api.put<Document>(`/projects/${projectId}/documents/${id}`, req);
}

export async function deleteDocument(
  projectId: ProjectId,
  id: DocumentId,
): Promise<void> {
  return api.delete(`/projects/${projectId}/documents/${id}`);
}

export async function listVersions(
  projectId: ProjectId,
  documentId: DocumentId,
): Promise<DocumentVersionListResponse> {
  return api.get<DocumentVersionListResponse>(
    `/projects/${projectId}/documents/${documentId}/versions`,
  );
}

export async function getDocumentVersion(
  projectId: ProjectId,
  documentId: DocumentId,
  version: number,
): Promise<DocumentVersion> {
  return api.get<DocumentVersion>(
    `/projects/${projectId}/documents/${documentId}/versions/${version}`,
  );
}

export async function listLinkedWorkItems(
  projectId: ProjectId,
  documentId: DocumentId,
): Promise<LinkedWorkItem[]> {
  return api.get<LinkedWorkItem[]>(
    `/projects/${projectId}/documents/${documentId}/links`,
  );
}

export async function linkWorkItem(
  projectId: ProjectId,
  documentId: DocumentId,
  workItemId: WorkItemId,
): Promise<void> {
  await api.post(`/projects/${projectId}/documents/${documentId}/links`, {
    work_item_id: workItemId,
  });
}

export async function listLinkedDocuments(
  projectId: ProjectId,
  workItemId: WorkItemId,
): Promise<LinkedDocument[]> {
  return api.get<LinkedDocument[]>(
    `/projects/${projectId}/work-items/${workItemId}/documents`,
  );
}

export async function unlinkWorkItem(
  projectId: ProjectId,
  documentId: DocumentId,
  workItemId: WorkItemId,
): Promise<void> {
  await api.delete(
    `/projects/${projectId}/documents/${documentId}/links/${workItemId}`,
  );
}

export async function scanRepoDocuments(
  projectId: ProjectId,
): Promise<ScanRepoDocumentsResponse> {
  return api.get<ScanRepoDocumentsResponse>(
    `/projects/${projectId}/github/documents`,
  );
}

export async function importDocuments(
  projectId: ProjectId,
  req: ImportDocumentsRequest,
): Promise<ImportDocumentsResponse> {
  return api.post<ImportDocumentsResponse>(
    `/projects/${projectId}/documents/import`,
    req,
  );
}

export async function reimportDocument(
  projectId: ProjectId,
  id: DocumentId,
): Promise<ImportDocumentResult> {
  return api.post<ImportDocumentResult>(
    `/projects/${projectId}/documents/${id}/reimport`,
    {},
  );
}

export async function clearDocumentStaleness(
  projectId: ProjectId,
  id: DocumentId,
): Promise<Document> {
  return api.post<Document>(
    `/projects/${projectId}/documents/${id}/clear-staleness`,
    {},
  );
}
